import React, { useRef } from 'react';
import { useDetectScroll } from '@saviynt/common';
import {
  Box,
  Button,
  Card,
  CardBody,
  FooterActionBar,
  Typography,
} from '@saviynt/design-system';

import {
  type LocalizationMessages,
  useInvertedLocalize,
} from '../../../shared/utils/localization';
import { useProviderEdit } from '../hooks';
import { type Providers } from '../types';

import illustrations from './illustrations';
import { ProviderEditView } from './ProviderEditView';

import './SSOEmptyState.css';

const PROVIDERS: Record<
  Providers,
  {
    description: LocalizationMessages;
    Illustration: React.FunctionComponent<React.SVGAttributes<SVGElement>>;
  }
> = {
  SAML: {
    description:
      'Enterprise login protocol for single sign-on across multiple apps via an identity provider.',
    Illustration: illustrations['create-new-organization'],
  },
};

interface Props {
  reloadProviders: () => void;
}

/** Empty-State UI for SSO Config Page */
export default function SSOEmptyState({ reloadProviders }: Props): JSX.Element {
  const l = useInvertedLocalize();
  const {
    activeProvider,
    canSave,
    isSaving,
    saveProvider,
    newProvider,
    cancelProviderEdit,
    saml,
  } = useProviderEdit([], reloadProviders);

  const sectionRef = useRef(null);
  const { isScrolled }: { isScrolled: boolean } = useDetectScroll(
    null,
    sectionRef
  );

  if (!activeProvider) {
    return (
      <main className='Auth_SSOEmptyState'>
        <Box className='Auth_SSOEmptyState_mainArea'>
          <Typography
            kind='h1'
            desktop='display2-light-d'
            mobile='display2-light-d'
            color='--color-foreground-secondary-medium'
            className='Auth_SSOEmptyState_h1'>
            {l('Start configuring your first SSO provider!')}
          </Typography>
          <Typography
            kind='body1'
            color='--color-background-secondary-bold'
            className='Auth_SSOEmptyState_description'>
            {l(
              'Single Sign-On (SSO) lets you log in once and access multiple apps or systems without signing in again. It makes login easier and more secure by using one trusted identity provider. Choose a protocol and set up seamless login for your users.'
            )}
          </Typography>
          <Box className='Auth_SSOEmptyState_cardCarousel'>
            {Object.entries(PROVIDERS).map(
              ([name, { description, Illustration }]) => (
                <Card key={name} className='Auth_SSOEmptyState_card'>
                  <CardBody className='Auth_SSOEmptyState_cardBody'>
                    <Illustration
                      role='img'
                      aria-label=''
                      className='Auth_SSOEmptyState_cardImage'
                    />
                    <Typography kind='h3'>{name}</Typography>
                    <Typography kind='body2'>{l(description)}</Typography>
                    <Button
                      kind='subtle'
                      ariaLabel={l(`Select {ssoProvider}`, {
                        ssoProvider: name,
                      })}
                      onClick={() => newProvider(name as Providers)}
                      className='Auth_SSOEmptyState_selectButton'>
                      {l('Select')}
                    </Button>
                  </CardBody>
                </Card>
              )
            )}
          </Box>
        </Box>
      </main>
    );
  }

  return (
    <Box className='Auth_SSOEmptyState_edit'>
      <main className='Auth_SSOEmptyState_editMain' ref={sectionRef}>
        <Typography kind='h1' className='Auth_SSOEmptyState_editH1'>
          {l('Configure {ssoProvider}', {
            ssoProvider: activeProvider,
          })}
        </Typography>
        <ProviderEditView
          activeProvider={activeProvider}
          saml={saml}
          isFullwidth
        />
      </main>
      <FooterActionBar size='large' isScrolled={isScrolled}>
        <Button kind='subtle' onClick={cancelProviderEdit}>
          {l('Back')}
        </Button>
        <Button
          kind='filled'
          onClick={saveProvider}
          isLoading={isSaving}
          isDisabled={!canSave}>
          {l('Save')}
        </Button>
      </FooterActionBar>
    </Box>
  );
}
